/**
 * BlockLinkParser Service
 *
 * Extracts linkable references from blocks to enable automatic relation creation.
 * Handles wikilinks, task references, embedded objects, and block references.
 */

import type { Block } from '../models/types';
import { db } from '../db/database';

export class BlockLinkParser {
  /**
   * Parse wikilinks [[Object Name]] from text content
   * Returns array of object IDs that are linked
   */
  async parseWikiLinks(content: string): Promise<string[]> {
    if (!content) return [];

    // Match [[Object Name]] or [[Object Name|Display Text]]
    const wikiLinkRegex = /\[\[([^\]|]+)(?:\|[^\]]+)?\]\]/g;
    const matches = [...content.matchAll(wikiLinkRegex)];

    if (matches.length === 0) return [];

    const objectNames = matches.map(m => m[1].trim());

    // Look up object IDs by name
    const objectIds: string[] = [];
    for (const name of objectNames) {
      const objects = await db.objects
        .where('name')
        .equals(name)
        .and(obj => !obj.deletedAt)
        .toArray();

      if (objects.length > 0) {
        // Take first match if multiple objects have same name
        objectIds.push(objects[0].id);
      }
    }

    return [...new Set(objectIds)]; // Remove duplicates
  }

  /**
   * Extract task IDs from task list block
   * Task lists store task IDs in content as JSON: { title: string, tasks: string[] } or string[]
   */
  extractTaskIds(block: Block): string[] {
    if (block.type !== 'tasklist') return [];

    try {
      const parsed = JSON.parse(block.content);

      // New format: { title: string, tasks: string[] }
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const tasks = parsed.tasks || [];
        return Array.isArray(tasks) ? tasks.filter(id => typeof id === 'string' && id !== '') : [];
      }

      // Old format: ["task-id-1", "task-id-2"]
      if (Array.isArray(parsed)) {
        return parsed.filter(id => typeof id === 'string' && id !== '');
      }
    } catch (error) {
      console.error('[BlockLinkParser] Failed to parse tasklist content:', error);
    }

    return [];
  }

  /**
   * Extract widget query results as linked objects
   * Database widgets and query widgets show linked objects
   */
  extractWidgetLinkedObjects(block: Block): string[] {
    // Widget blocks that link to other objects
    const widgetTypes = ['widget-tasks', 'widget-database', 'widget-query'];
    if (!widgetTypes.includes(block.type)) return [];

    // Widgets store linked object IDs in metadata
    const linkedIds = block.metadata?.linkedObjectIds || block.metadata?.resultIds || [];
    if (!Array.isArray(linkedIds)) return [];

    return linkedIds.filter(id => typeof id === 'string');
  }

  /**
   * Extract all object references from a block
   * Returns object IDs that should be linked via relations
   */
  async extractLinkedObjectIds(block: Block): Promise<string[]> {
    const linkedIds: string[] = [];

    // 1. Parse wikilinks from content
    const wikiLinkIds = await this.parseWikiLinks(block.content);
    linkedIds.push(...wikiLinkIds);

    // 2. Extract task IDs from task lists
    const taskIds = this.extractTaskIds(block);
    linkedIds.push(...taskIds);

    // 3. Extract widget-linked objects
    const widgetIds = this.extractWidgetLinkedObjects(block);
    linkedIds.push(...widgetIds);

    // 4. Check title for wikilinks too
    if (block.title) {
      const titleLinks = await this.parseWikiLinks(block.title);
      linkedIds.push(...titleLinks);
    }

    return [...new Set(linkedIds)]; // Remove duplicates
  }

  /**
   * Extract all references from all blocks of an object
   * Returns grouped references by type
   */
  async extractObjectReferences(objectId: string): Promise<{
    taskIds: string[];
    wikiLinkIds: string[];
    widgetIds: string[];
    allLinkedIds: string[];
  }> {
    const blocks = await db.blocks
      .where({ objectId })
      .and(b => !b.deletedAt)
      .toArray();

    const taskIds: string[] = [];
    const wikiLinkIds: string[] = [];
    const widgetIds: string[] = [];

    for (const block of blocks) {
      // Task lists
      if (block.type === 'tasklist') {
        const ids = this.extractTaskIds(block);
        taskIds.push(...ids);
      }

      // Wikilinks in content
      const wikiIds = await this.parseWikiLinks(block.content);
      wikiLinkIds.push(...wikiIds);

      // Title wikilinks
      if (block.title) {
        const titleWikiIds = await this.parseWikiLinks(block.title);
        wikiLinkIds.push(...titleWikiIds);
      }

      // Widget-linked objects
      const wIds = this.extractWidgetLinkedObjects(block);
      widgetIds.push(...wIds);
    }

    // Remove duplicates
    const uniqueTaskIds = [...new Set(taskIds)];
    const uniqueWikiIds = [...new Set(wikiLinkIds)];
    const uniqueWidgetIds = [...new Set(widgetIds)];
    const allLinkedIds = [...new Set([...uniqueTaskIds, ...uniqueWikiIds, ...uniqueWidgetIds])];

    return {
      taskIds: uniqueTaskIds,
      wikiLinkIds: uniqueWikiIds,
      widgetIds: uniqueWidgetIds,
      allLinkedIds,
    };
  }

  /**
   * Find objects that link TO this object (backlinks)
   * Scans all blocks for wikilinks pointing to this object
   */
  async findBacklinks(objectId: string): Promise<string[]> {
    // Get the object name
    const object = await db.objects.get(objectId);
    if (!object) return [];

    const objectName = object.name;
    const backlinkIds: string[] = [];

    // Search all blocks for wikilinks to this object name
    const allBlocks = await db.blocks
      .where('deletedAt')
      .equals(undefined as any)
      .toArray();

    for (const block of allBlocks) {
      // Skip blocks from the same object
      if (block.objectId === objectId) continue;

      // Check content for wikilinks to this object
      const linkedIds = await this.parseWikiLinks(block.content);
      if (linkedIds.includes(objectId)) {
        backlinkIds.push(block.objectId);
      }

      // Check title too
      if (block.title) {
        const titleLinkedIds = await this.parseWikiLinks(block.title);
        if (titleLinkedIds.includes(objectId)) {
          backlinkIds.push(block.objectId);
        }
      }
    }

    return [...new Set(backlinkIds)]; // Remove duplicates
  }
}

// Singleton instance
export const blockLinkParser = new BlockLinkParser();
